import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { startCreditPackPurchase, BillingError, type CreditPack } from "@/lib/billing";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

interface CreditPackButtonProps {
  pack: CreditPack;
  className?: string;
  children?: React.ReactNode;
  variant?: "primary" | "secondary";
}

export function CreditPackButton({ pack, className, children, variant = "secondary" }: CreditPackButtonProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  async function handleClick() {
    if (!user) {
      sessionStorage.setItem("doclens.postLoginIntent", JSON.stringify({ kind: "credit_pack", pack }));
      sessionStorage.setItem("doclens.postLoginRedirect", "/pricing");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      await startCreditPackPurchase(pack);
    } catch (err) {
      const message = err instanceof BillingError ? err.message : "Could not start checkout";
      toast.error(message);
      setLoading(false);
    }
  }

  const base =
    variant === "primary"
      ? "bg-brand text-white hover:bg-brand-700"
      : "border border-neutral-300 hover:bg-neutral-50 text-neutral-900";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={cn(
        "block text-center rounded-lg px-4 py-2.5 font-medium transition",
        base,
        loading && "opacity-60 cursor-wait",
        className,
      )}
    >
      {loading ? "Redirecting…" : children ?? "Buy credits"}
    </button>
  );
}
